import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TimeRangeInput, type TimeRangeInputValue, type TimeRangePreset, computesCrossesMidnight } from '@/components/TimeRangeInput';
import { DOW_LABELS } from '@/lib/calendar-day';
import { usePreferenceInputMode } from '@/hooks/usePreferenceInputMode';
import { useShiftPatternPresets } from '@/hooks/useShiftPatternPresets';
import { computeStandingPatternCoverage } from '@/lib/standing-pattern-coverage';
import { buildDisplayPeople, type Entry, type Person, type SupportCandidate } from '@/lib/standing-pattern-people';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/shift/standing-patterns` (v0.4 章④ F-3、§5-15 含意2-2、D-106)。
 * ⚠️ 「個人の希望入力フォーム」にしてはならない — 店舗単位で調整・合意された結果として、
 * 店長が店舗の週間カバレッジを見ながら調整するビュー。個人自由登録だと全員が週末休みになる。
 * PUT は「店舗×期間=完全置換」と同じ思想で、画面上での複数編集をまとめて1回で保存する。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-5 / §4-11
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type TemplateSummary = { template_id: string; code: string; label: string; node_id: string | null; sort_order: number };
type TemplateEntry = { day_of_week: number; start_time: string; end_time: string; crosses_midnight: boolean; preference_level_id: string };
type PreferenceLevel = { level_id: string; code: string; label: string; is_available: boolean; weight: number; sort_order: number };
// v0.4 追加要件⑫ 増分④ (D-382、-detail.md §3-3): 曜日別充足プレビュー。
type StaffingPreviewCandidate = { employee_id: string | null; external_person_id: string | null; display_name: string };
type StaffingPreviewDay = {
  day_of_week: number; representative_date: string | null; satisfied: boolean | null;
  candidates_not_yet_scheduled: StaffingPreviewCandidate[];
};
type StaffingPreviewResponse = { node_id: string; by_day_of_week: StaffingPreviewDay[] };
// v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): 保存前の差分・影響プレビュー。
type PersonDiffEntry = {
  employee_id: string | null; external_person_id: string | null; display_name: string;
  added: Array<{ day_of_week: number; start_time: string; end_time: string; preference_level_id: string }>;
  // ⚠️ バックエンドは crosses_midnight/shift_pattern_id/template_id も持つが (D-385の完全一致比較用)、
  // 表示に使うのは day_of_week/start_time/end_time のみ。
  removed: Array<{ day_of_week: number; start_time: string; end_time: string }>;
};
type DiffSummary = { changed_person_count: number; persons: PersonDiffEntry[] };
type ContractedImpact = {
  employee_id: string; before: number | null; after: number | null;
  flex_slots_before: number | null; flex_slots_after: number | null;
};
type StaffingByDowImpact = {
  day_of_week: number; representative_date: string | null; required: number | null;
  available_before: number | null; available_after: number | null;
  satisfied_before: boolean | null; satisfied_after: boolean | null;
};
type ImpactSummary = { contracted: ContractedImpact[]; staffing_by_dow: StaffingByDowImpact[] };
type DiffPreviewResponse = { node_id: string; diff_summary: DiffSummary; impact_summary: ImpactSummary };

type DraftEntry = {
  localId: string; employee_id: string | null; external_person_id: string | null;
  day_of_week: number; start_time: string; end_time: string; crosses_midnight: boolean; preference_level_id: string;
  shift_pattern_id: string | null;
  // v0.4 追加要件⑫ 増分③ (D-379): この枠がテンプレートから展開されたものならそのID。
  // 個別に手で編集した枠は null に落とす (D-379「手で直した行は source='manual' に落とす」)。
  template_id: string | null;
  // §8-221対策: サーバーから読み込んだ既存枠はサーバー解決済みのラベル/is_availableを持つ
  // (廃止済み選好レベルでも正しく表示できる)。この編集セッション中に新規追加・変更した枠は
  // undefined のままでよく、その場合は現行マスタ一覧 (levelById、常に現行のみ) から解決する
  // (新規選択はどのみち現行の選択肢からしか選べないため)。
  preference_level_label?: string | null;
  preference_level_is_available?: boolean | null;
};

const personKey = (p: { employee_id: string | null; external_person_id: string | null }) => (p.employee_id ? `E:${p.employee_id}` : `X:${p.external_person_id}`);
let localIdCounter = 0;
const nextLocalId = () => `local-${++localIdCounter}`;

// v0.4 追加要件⑫ 増分⑤: coverage は null (週所定未設定、§6-0原則3) がありうるため「—」で示す。
const formatCoveragePercent = (coverage: number | null): string => (coverage == null ? '—' : `${Math.round(coverage * 100)}%`);

/** v0.4 追加要件⑫ 増分⑤ (design-expert/design-and-code レビュー指摘、CRITICAL/MEDIUM):
 *  satisfied は null (8週先まで代表日が選定できず評価不能) をとりうるが、`=== false` の三項演算子だと
 *  null が「充足」と誤表示されていた。既存のプレビュー行 (増分④) は「代表日なし」を明示しており、
 *  ここでも同じ扱いに揃える。 */
function SatisfiedLabel({ satisfied, emphasize }: { satisfied: boolean | null; emphasize?: boolean }) {
  if (satisfied === null) return <span className="text-text-sub">判定不能</span>;
  if (satisfied === false) return <span className={`text-alert-text ${emphasize ? 'font-semibold' : ''}`}>不足</span>;
  return <span>充足</span>;
}
// v0.4 追加要件⑫ 増分④ (D-382): 曜日別充足プレビューの基準日。表示上の「今日」でよく
// (業務データの計算はサーバー側の staffing-preview エンドポイントが as_of を受け取って行う、§0-2)。
const today = (): string => new Date().toISOString().slice(0, 10);

const peopleToDraft = (people: Person[]): DraftEntry[] =>
  people.flatMap((p) => p.entries.map((e) => ({
    localId: nextLocalId(), employee_id: p.employee_id, external_person_id: p.external_person_id,
    day_of_week: e.day_of_week, start_time: e.start_time, end_time: e.end_time,
    crosses_midnight: e.crosses_midnight, preference_level_id: e.preference_level_id,
    shift_pattern_id: e.shift_pattern_id, template_id: e.template_id,
    preference_level_label: e.preference_level_label, preference_level_is_available: e.preference_level_is_available,
  })));

function PreferenceLevelsPanel({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<{ level_id: string | null; code: string; label: string; is_available: boolean; weight: string; sort_order: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PreferenceLevel | null>(null);

  const { data, isLoading } = useQuery<{ preference_levels: PreferenceLevel[] }>({
    queryKey: ['preference-levels'], queryFn: () => apiFetch('/preference-levels'), enabled: expanded,
  });
  const levels = data?.preference_levels ?? [];

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = { code: d.code.trim(), label: d.label.trim(), is_available: d.is_available, weight: Number(d.weight) || 0, sort_order: Number(d.sort_order) || 0 };
      return d.level_id
        ? apiFetch(`/preference-levels/${d.level_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/preference-levels', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preference-levels'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/preference-levels/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preference-levels'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="border border-surface-border rounded-lg bg-white">
      <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm font-semibold text-text-body">
        選好レベルの設定 (不可・許容・希望 等の段階)
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {expanded && (
        <div className="px-3 pb-4 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-text-sub">段階の数・ラベル・重みは自由に定義できます。「不可」は割当を禁止する硬い制約になります。</p>
            {canManage && (
              <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />}
                onClick={() => setDraft({ level_id: null, code: '', label: '', is_available: true, weight: '0', sort_order: String(levels.length) })}>
                段階を追加
              </Button>
            )}
          </div>
          {isLoading ? (
            <div className="text-xs text-text-sub">読み込み中…</div>
          ) : levels.length === 0 ? (
            <p className="text-xs text-text-sub">選好レベルがまだありません。</p>
          ) : (
            <ul className="space-y-1.5">
              {levels.map((l) => (
                <li key={l.level_id} className="flex items-center justify-between gap-2 text-xs border border-surface-line rounded p-2">
                  <span>
                    <span className="font-semibold text-text-body">{l.label}</span>
                    {' '}({l.code}) {!l.is_available && <span className="text-danger-text">割当不可</span>} 重み:{l.weight}
                  </span>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" aria-label="編集" onClick={() => setDraft({ level_id: l.level_id, code: l.code, label: l.label, is_available: l.is_available, weight: String(l.weight), sort_order: String(l.sort_order) })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={12} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(l)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={12} /></button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Modal open={!!draft} title={draft?.level_id ? '選好レベルを編集' : '選好レベルを追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: preferred" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 希望" />
            </div>
            <label className="flex items-center gap-2 text-sm min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={draft.is_available} onChange={(e) => setDraft({ ...draft, is_available: e.target.checked })} />
              この段階は割当可能 (オフにすると「不可」= 硬い制約になります)
            </label>
            <div>
              <label className="block text-sm text-text-sub mb-1">重み (選好の充足度計算に使用)</label>
              <input type="number" step="0.1" value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: e.target.value })} className={inputCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="選好レベルを削除" message={`「${deleteTarget?.label}」を削除します。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.level_id)} />
    </div>
  );
}

function EntryEditorModal({ context, levels, presets, hideManualInput, onClose, onSave, onRemove }: {
  context: { personKey: string; day_of_week: number; entry: DraftEntry | null };
  levels: PreferenceLevel[];
  presets: TimeRangePreset[];
  hideManualInput: boolean;
  onClose: () => void;
  onSave: (value: TimeRangeInputValue, levelId: string, shiftPatternId: string | null) => void;
  onRemove: (() => void) | null;
}) {
  const [value, setValue] = useState<TimeRangeInputValue>({
    startTime: context.entry?.start_time ?? '09:00', endTime: context.entry?.end_time ?? '17:00',
  });
  const [levelId, setLevelId] = useState(context.entry?.preference_level_id ?? levels[0]?.level_id ?? '');
  // v0.4 追加7要件⑤ (D-235/Q-179): パターンを選ぶと当該ノードの実効時刻を流し込む
  // (TimeRangeInput の presets 機構を利用)。選択後に手修正しても shift_pattern_id は保持し
  // 「由来」として残す。
  const [shiftPatternId, setShiftPatternId] = useState<string | null>(context.entry?.shift_pattern_id ?? null);
  const canSave = !!value.startTime && !!value.endTime && !!levelId;
  // §8-221/§8-222対策 (design-and-codeレビュー指摘、2026-09-26): 廃止済みの選好レベルを参照する
  // 既存枠を開くと、Select の value が現行マスタ一覧 (levels) のどの選択肢とも一致せず空欄になって
  // いた (WorkRecordModal.tsx の理由コードと同型)。編集対象の現在値をラベル付きで選択肢に追加する。
  // 接頭辞 (末尾ではなく先頭) にするのは、SelectTrigger のtruncateでラベルが長いと末尾の注記が
  // 見えなくなるため (design-expertレビュー指摘)。
  const selectableLevels = useMemo<PreferenceLevel[]>(() => {
    const entryLevelId = context.entry?.preference_level_id;
    if (!entryLevelId || levels.some((l) => l.level_id === entryLevelId)) return levels;
    return [
      {
        level_id: entryLevelId, code: '', sort_order: -1,
        is_available: context.entry?.preference_level_is_available ?? true,
        weight: 0,
        label: `(廃止) ${context.entry?.preference_level_label ?? '削除済みの選好レベル'}`,
      },
      ...levels,
    ];
  }, [context.entry, levels]);

  return (
    <Modal open title={`${DOW_LABELS[context.day_of_week]}曜日の枠`} onClose={onClose} size="sm"
      footer={(
        <>
          {onRemove && <Button variant="ghost" onClick={onRemove}>この枠を削除</Button>}
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave} onClick={() => onSave(value, levelId, shiftPatternId)}>反映</Button>
        </>
      )}>
      <div className="space-y-4">
        <TimeRangeInput
          value={value} onChange={setValue}
          presets={presets} selectedPresetId={shiftPatternId} onPresetSelect={setShiftPatternId}
          hideManualInput={hideManualInput}
        />
        <div>
          <label className="block text-sm text-text-sub mb-1">選好レベル</label>
          <Select value={levelId || undefined} onValueChange={setLevelId}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
            <SelectContent>
              {selectableLevels.map((l) => <SelectItem key={l.level_id} value={l.level_id}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-text-sub">「反映」を押すと画面上の下書きに反映されます。実際に保存するにはページ下部の「保存」を押してください。</p>
      </div>
    </Modal>
  );
}

/**
 * v0.4 追加要件⑫ 増分③ (D-379、-detail.md §3-4): 「テンプレートから展開」。新規の書込み
 * エンドポイントは作らず、テンプレートの曜日別時刻 (GET .../entries) を取得して対象人物の
 * 週間行を丸ごと置き換える下書きを作る。保存 (永続化) は既存の PUT /standing-patterns が担う。
 */
function ApplyTemplateModal({
  nodeId, personDisplayName, onClose, onApply,
}: {
  nodeId: string; personDisplayName: string;
  onClose: () => void; onApply: (templateId: string, entries: TemplateEntry[]) => void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading } = useQuery<{ templates: TemplateSummary[] }>({
    queryKey: ['standing-pattern-templates', nodeId],
    queryFn: () => apiFetch(`/standing-pattern-templates?node_id=${encodeURIComponent(nodeId)}`),
  });
  const templates = data?.templates ?? [];

  const applyMut = useMutation({
    mutationFn: () => apiFetch<{ entries: TemplateEntry[] }>(`/standing-pattern-templates/${selectedId}/entries`),
    onSuccess: (res) => onApply(selectedId, res.entries),
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : 'テンプレートの取得に失敗しました'); setConfirming(false); },
  });

  return (
    <>
      <Modal open={!confirming} title="テンプレートから展開" onClose={onClose} size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={onClose}>キャンセル</Button>
            <Button variant="primary" disabled={!selectedId} onClick={() => setConfirming(true)}>次へ</Button>
          </>
        )}>
        <div className="space-y-3">
          <p className="text-sm text-text-sub">{personDisplayName} さんの週間の枠を、選んだテンプレートの内容で丸ごと置き換えます。</p>
          {isLoading ? (
            <div className="text-sm text-text-sub">読み込み中…</div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-text-sub">この店舗で使えるテンプレートがまだありません。「設定 &gt; 恒常パターンテンプレート」から作成してください。</p>
          ) : (
            <Select value={selectedId || undefined} onValueChange={setSelectedId}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="テンプレートを選ぶ" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </Modal>
      <ConfirmDialog open={confirming} title="テンプレートを適用"
        message={`${personDisplayName} さんの現在の週間の枠はすべて削除され、テンプレートの内容に置き換わります。ページ下部の「保存」を押すまでは確定しません。`}
        confirmLabel="適用する" loading={applyMut.isPending}
        onClose={() => setConfirming(false)} onConfirm={() => applyMut.mutate()} />
    </>
  );
}

export function ShiftStandingPatternsPage() {
  const user = useAuthStore((s) => s.user);
  // 恒常パターン本体 (PUT /standing-patterns) は node_id 単位の判定のため own_store でも自店は編集可。
  const canManage = hasPermission(user?.effective_permissions, 'shift_preference', 'manage');
  // ⚠️ TD-22: 選好レベルマスタ (POST/PUT/DELETE /preference-levels) だけは
  // requireTenantWideManage (routes/preference-levels.ts) で全店舗スコープを要求する
  // (選好レベルはテナント全体で共有されるマスタのため)。
  const canManagePreferenceLevels = hasTenantWidePermission(user?.effective_permissions, 'shift_preference', 'manage');
  const qc = useQueryClient();
  const [nodeId, setNodeId] = useState('');
  const [draft, setDraft] = useState<DraftEntry[] | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [editContext, setEditContext] = useState<{ personKey: string; day_of_week: number; entry: DraftEntry | null } | null>(null);
  // v0.4 追加要件⑫ 増分③ (D-379): 「テンプレートから展開」の対象人物
  const [templateApplyContext, setTemplateApplyContext] = useState<{ employee_id: string | null; external_person_id: string | null; display_name: string } | null>(null);
  // v0.4 追加要件⑫ 増分④ (D-382): 曜日別充足プレビュー。previewedEntriesJson は「プレビューを
  // 実行した時点の draft」を保持し、以後の編集で内容が変わったら preview を古いものとして扱う
  // (破棄はせず、画面上で「内容が変わりました」と示すだけに留める)。
  const [preview, setPreview] = useState<StaffingPreviewResponse | null>(null);
  const [previewedEntriesJson, setPreviewedEntriesJson] = useState<string | null>(null);
  // v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): 保存ボタン押下時の差分・影響の確認ダイアログ。
  // null = 未表示、値あり = 保存前プレビュー結果を表示中。
  const [confirmSave, setConfirmSave] = useState<DiffPreviewResponse | null>(null);

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);

  const { data, isLoading } = useQuery<{ node_id: string; people: Person[]; support_candidates: SupportCandidate[] }>({
    queryKey: ['standing-patterns', nodeId], queryFn: () => apiFetch(`/standing-patterns?node_id=${encodeURIComponent(nodeId)}`), enabled: !!nodeId,
  });
  const { data: levelsData } = useQuery<{ preference_levels: PreferenceLevel[] }>({ queryKey: ['preference-levels'], queryFn: () => apiFetch('/preference-levels') });
  const levels = levelsData?.preference_levels ?? [];

  // v0.4 追加要件⑩ (D-325/D-326、user判断で⑫観察事項として追加): 「応援候補」から店長が選んで
  // グリッドへ一時的に行を追加する (ShiftBoardPage.tsx と同じ思想、DBへは何も書き込まない —
  // 実際に永続化されるのは選んだ人物の枠を「保存」した時点のみ、バックエンドの
  // validateStandingPatternEntries は既に応援候補を許可済み)。
  const supportCandidates = data?.support_candidates ?? [];
  const [addedSupportEmployeeIds, setAddedSupportEmployeeIds] = useState<Set<string>>(new Set());
  useEffect(() => { setAddedSupportEmployeeIds(new Set()); }, [nodeId]);

  // v0.4 追加7要件⑤ (D-235): パターンから選ぶ Select の出し分け
  const inputMode = usePreferenceInputMode(nodeId || null);
  const { presets } = useShiftPatternPresets(nodeId || null, inputMode !== 'time_range');

  useEffect(() => {
    if (data && loadedKey !== nodeId) {
      setDraft(peopleToDraft(data.people));
      setLoadedKey(nodeId);
    }
  }, [data, nodeId, loadedKey]);

  const people = data?.people ?? [];
  // v0.4 追加要件⑩: 表示用の行一覧 = ロースター + 店長が「応援候補」から追加した人物 (ロジックは
  // `lib/standing-pattern-people.ts` に抽出、テストは同ファイル横の *.test.ts 参照)。
  // isDirty の比較や saveMut の payload 生成は draft (実際に追加された枠) のみを見るため、この配列を
  // 混ぜても「何も枠を追加していない応援候補」だけでは保存対象にならない。
  const displayPeople = useMemo(
    () => buildDisplayPeople(people, supportCandidates, addedSupportEmployeeIds),
    [people, supportCandidates, addedSupportEmployeeIds],
  );
  const stripLocalId = (entries: DraftEntry[]) => entries
    .map((e) => ({
      employee_id: e.employee_id, external_person_id: e.external_person_id, day_of_week: e.day_of_week,
      start_time: e.start_time, end_time: e.end_time, crosses_midnight: e.crosses_midnight,
      preference_level_id: e.preference_level_id, shift_pattern_id: e.shift_pattern_id, template_id: e.template_id,
    }))
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const isDirty = !!draft && !!data
    && JSON.stringify(stripLocalId(draft)) !== JSON.stringify(stripLocalId(peopleToDraft(data.people)));

  // v0.4 追加要件⑫ 増分⑤: PUT '/standing-patterns' と diff-preview の両方が同じ entries 形式を
  // 送るため、変換ロジックを1箇所にまとめる。
  const buildEntriesPayload = () => (draft ?? []).map((e) => ({
    employee_id: e.employee_id ?? undefined, external_person_id: e.external_person_id ?? undefined,
    day_of_week: e.day_of_week, start_time: e.start_time, end_time: e.end_time,
    crosses_midnight: e.crosses_midnight, preference_level_id: e.preference_level_id,
    shift_pattern_id: e.shift_pattern_id, template_id: e.template_id,
  }));

  const saveMut = useMutation({
    mutationFn: () => apiFetch<
      { node_id: string; added: number; changed: number; removed: number }
      | { node_id: string; status: 'pending_approval'; approval_request_id: string }
    >(`/standing-patterns?node_id=${encodeURIComponent(nodeId)}`, {
      method: 'PUT',
      body: JSON.stringify({ entries: buildEntriesPayload() }),
    }),
    onSuccess: async (res) => {
      // ⚠️ invalidateQueries の完了を待たずに setLoadedKey(null) を呼ぶと、再取得前の
      // 古い data で下の useEffect が発火して draft を巻き戻してしまう
      // (保存直後の一瞬だけ「未保存の変更があります」が誤って再表示される表示上のバグ、
      // タスクチップ task_c3220814 参照)。await してからガードを外すことで、
      // useEffect が発火する時点で data が既に最新化されていることを保証する。
      await qc.invalidateQueries({ queryKey: ['standing-patterns', nodeId] });
      setLoadedKey(null);
      setConfirmSave(null);
      // D-196: 承認ワークフローが有効な場合は 202 pending_approval が返り、変更はまだ反映されない
      if (!('added' in res)) {
        toast.success('承認申請を送信しました。承認され次第、内容が反映されます。');
        return;
      }
      toast.success(`保存しました (追加${res.added}・変更${res.changed}・削除${res.removed})`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  // v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): 保存ボタン押下時にまず差分・影響を取得し、
  // 確認ダイアログで表示する。この結果は表示専用であり、実際の保存 (PUT) はサーバーが自分で
  // 再計算した値を使う (この結果を送り返すことはしない)。
  const diffPreviewMut = useMutation({
    mutationFn: () => apiFetch<DiffPreviewResponse>(`/standing-patterns/diff-preview?node_id=${encodeURIComponent(nodeId)}`, {
      method: 'POST',
      body: JSON.stringify({ entries: buildEntriesPayload(), as_of: today() }),
    }),
    onSuccess: (res) => setConfirmSave(res),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '差分の確認に失敗しました'),
  });

  // v0.4 追加要件⑩ (4並列レビュー QA/Test/design-and-code が独立に指摘した consensus finding):
  // draft を data.people (ロースターのみ) へ戻すだけでは、応援候補から追加した空行が
  // addedSupportEmployeeIds に残ったままテーブルに居座り続ける (support_candidates は
  // 引き続きそのemployeeを返すためチップも再出現しない)。破棄は「この画面で行った操作を
  // 全て取り消す」動作であるべきなので、ここでも空集合へ戻す。
  const discard = () => { if (data) { setDraft(peopleToDraft(data.people)); setAddedSupportEmployeeIds(new Set()); } };

  const openEntry = (pKey: string, day: number, entry: DraftEntry | null) => setEditContext({ personKey: pKey, day_of_week: day, entry });

  // v0.4 追加要件⑫ 増分④ (D-382、-detail.md §3-3): 編集中 (未保存) のグリッド全体をサーバーへ
  // 送り、曜日ごとの代表日で必要人員の充足を評価してもらう。保存はしない・キー入力のたびには呼ばない
  // (「保存前プレビュー」ボタン起点)。
  const previewMut = useMutation({
    mutationFn: () => {
      const entries = (draft ?? []).map((e) => ({
        employee_id: e.employee_id ?? undefined, external_person_id: e.external_person_id ?? undefined,
        day_of_week: e.day_of_week, start_time: e.start_time, end_time: e.end_time,
        crosses_midnight: e.crosses_midnight, preference_level_id: e.preference_level_id,
      }));
      return apiFetch<StaffingPreviewResponse>(`/standing-patterns/staffing-preview?node_id=${encodeURIComponent(nodeId)}`, {
        method: 'POST',
        body: JSON.stringify({ entries, as_of: today() }),
      }).then((res) => { setPreviewedEntriesJson(JSON.stringify(entries)); return res; });
    },
    onSuccess: (res) => setPreview(res),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '充足プレビューの取得に失敗しました'),
  });

  // v0.4 追加要件⑫ 増分④: 玉突み候補チップをクリックすると、その候補者・その曜日の枠を追加する
  // エディタを開く (「玉突み先の発見をシステムが担う部分」、正典 D-382)。候補者は必ず people (draft
  // が参照するロースター) に含まれる (draft の entries はすべて既存の people 行から作られるため)。
  const openCandidateEntry = (day: number, candidate: StaffingPreviewCandidate) => {
    if (!canManage) return;
    openEntry(personKey({ employee_id: candidate.employee_id, external_person_id: candidate.external_person_id }), day, null);
  };

  const applyEntry = (value: TimeRangeInputValue, levelId: string, shiftPatternId: string | null) => {
    if (!editContext || !draft) return;
    const p = displayPeople.find((pp) => personKey(pp) === editContext.personKey);
    if (!p) return;
    const crossesMidnight = computesCrossesMidnight(value);
    // v0.4 追加要件⑫ 増分③ (D-379): 個別に手で編集/追加した枠は template_id を null に落とす
    // (「手で直した行は source='manual' に落とす」)。
    if (editContext.entry) {
      setDraft(draft.map((e) => (e.localId === editContext.entry!.localId
        ? { ...e, start_time: value.startTime!, end_time: value.endTime!, crosses_midnight: crossesMidnight, preference_level_id: levelId, shift_pattern_id: shiftPatternId, template_id: null }
        : e)));
    } else {
      setDraft([...draft, {
        localId: nextLocalId(), employee_id: p.employee_id, external_person_id: p.external_person_id,
        day_of_week: editContext.day_of_week, start_time: value.startTime!, end_time: value.endTime!,
        crosses_midnight: crossesMidnight, preference_level_id: levelId, shift_pattern_id: shiftPatternId, template_id: null,
      }]);
    }
    setEditContext(null);
  };

  const removeEntry = () => {
    if (!editContext?.entry || !draft) return;
    setDraft(draft.filter((e) => e.localId !== editContext.entry!.localId));
    setEditContext(null);
  };

  // v0.4 追加要件⑫ 増分③ (D-379、-detail.md §3-4): テンプレートの週間内容で対象人物の枠を丸ごと
  // 置き換える。保存 (永続化) はしない — 既存の draft へ反映するだけで、ページ下部の「保存」で
  // 初めて PUT /standing-patterns を通じて永続化される (既存の全編集フローと同じ)。
  const applyTemplateToPerson = (templateId: string, templateEntries: TemplateEntry[]) => {
    if (!draft || !templateApplyContext) return;
    const pKey = personKey(templateApplyContext);
    const withoutPerson = draft.filter((e) => personKey(e) !== pKey);
    const newEntries: DraftEntry[] = templateEntries.map((te) => ({
      localId: nextLocalId(),
      employee_id: templateApplyContext.employee_id, external_person_id: templateApplyContext.external_person_id,
      day_of_week: te.day_of_week, start_time: te.start_time, end_time: te.end_time,
      crosses_midnight: te.crosses_midnight, preference_level_id: te.preference_level_id,
      shift_pattern_id: null, template_id: templateId,
    }));
    setDraft([...withoutPerson, ...newEntries]);
    setTemplateApplyContext(null);
    toast.success('テンプレートを適用しました (ページ下部の「保存」を押すまでは確定しません)');
  };

  const levelById = useMemo(() => new Map(levels.map((l) => [l.level_id, l])), [levels]);
  // v0.4 追加要件⑫ 増分② (D-380): 編集中の draft からリアルタイムに充足/融通枠を再計算する
  // (サーバー往復なし、apps/api 側と同じロジックのミラー実装、§4-2)。
  const preferenceLevelMap = useMemo(
    () => new Map(levels.map((l) => [l.level_id, { isAvailable: l.is_available, weight: l.weight }])),
    [levels],
  );
  const coverageByPerson = useMemo(() => {
    // ⚠️ QA レビュー指摘 (MEDIUM): 週所定0日 (ゼロ日契約) は -detail.md §4-1 のとおり
    // 「入れる日は全部が任意」であり警告を出してはならない。しかし entries が0件だと
    // flexSlots も0になり、「所定ぴったりで警告すべき人」と区別がつかなくなる
    // (computeStandingPatternCoverage の戻り値だけでは判別不能)。isZeroDayContract を
    // 別途保持し、警告表示 (バッジ色・ヘッダー集計) からゼロ日契約者を除外する。
    const map = new Map<string, { coverage: number | null; flexSlots: number | null; isZeroDayContract: boolean }>();
    for (const p of displayPeople) {
      const pKey = personKey(p);
      const entriesForPerson = (draft ?? []).filter((e) => personKey(e) === pKey);
      const weeklyContractedWorkDays = p.weekly_contracted_work_days?.value ?? null;
      const result = computeStandingPatternCoverage(
        entriesForPerson.map((e) => ({ dayOfWeek: e.day_of_week, preferenceLevelId: e.preference_level_id })),
        preferenceLevelMap,
        weeklyContractedWorkDays,
      );
      map.set(pKey, { ...result, isZeroDayContract: weeklyContractedWorkDays === 0 });
    }
    return map;
  }, [displayPeople, draft, preferenceLevelMap]);
  const zeroFlexSlotCount = [...coverageByPerson.values()].filter((v) => v.flexSlots === 0 && !v.isZeroDayContract).length;

  // v0.4 追加要件⑫ 増分④ (D-382): プレビュー実行後に draft がさらに編集されたら「内容が変わった」
  // ことを示す (結果を捨てはしない、-detail.md §3-3 の「保存前プレビュー」用途に沿って明示的に
  // 再実行させる)。
  const currentPreviewInputJson = useMemo(() => JSON.stringify((draft ?? []).map((e) => ({
    employee_id: e.employee_id ?? undefined, external_person_id: e.external_person_id ?? undefined,
    day_of_week: e.day_of_week, start_time: e.start_time, end_time: e.end_time,
    crosses_midnight: e.crosses_midnight, preference_level_id: e.preference_level_id,
  }))), [draft]);
  const previewByDayOfWeek = useMemo(() => new Map((preview?.by_day_of_week ?? []).map((d) => [d.day_of_week, d])), [preview]);
  const isPreviewStale = !!preview && previewedEntriesJson !== currentPreviewInputJson;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">恒常パターン調整</h2>
        <p className="text-sm text-text-sub mt-1">
          社員・アルバイトの「基本の働き方」を、店舗の週間カバレッジを見ながら調整します。個人の希望を集める画面ではなく、店舗内で調整・合意された結果を管理する画面です。
        </p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (編集には店舗の管理権限が必要です)。</p>}
        {/* ⚠️ TD-22: 選好レベルマスタはテナント全体で共有されるため全店舗スコープを要求する。
            注記が無いと店長には該当パネルのボタンだけが無言で消える。 */}
        {canManage && !canManagePreferenceLevels && (
          <p className="text-xs text-text-sub mt-1">恒常パターンは編集できますが、選好レベルの追加・編集・削除には全店舗の管理権限が必要です。</p>
        )}
      </div>

      <PreferenceLevelsPanel canManage={canManagePreferenceLevels} />

      <div className="min-w-0 w-full sm:w-72">
        <label className="block text-xs text-text-sub mb-1">対象店舗</label>
        <Select value={nodeId || undefined} onValueChange={(v) => { setNodeId(v); setLoadedKey(null); setPreview(null); setPreviewedEntriesJson(null); }}>
          <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
          <SelectContent>
            {flatNodes.map(({ node, depth }) => (
              <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!nodeId ? (
        <p className="text-sm text-text-sub">対象店舗を選んでください。</p>
      ) : isLoading || !draft ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-3">
          {/* v0.4 追加要件⑩ (D-325/D-326): 応援候補。ShiftBoardPage.tsx と同じ表示・追加操作
              (DBへは何も書き込まない — 実際に永続化されるのは枠を追加して「保存」した時点のみ)。
              人員が0名でも表示する (応援可能な社員がいるのに気づけない、という観察事項への対応)。 */}
          {canManage && supportCandidates.some((sc) => !addedSupportEmployeeIds.has(sc.employee_id)) && (
            <div className="flex flex-wrap items-center gap-2 p-3 border border-surface-border rounded-lg bg-white">
              <span className="text-xs text-text-sub shrink-0">応援候補:</span>
              {supportCandidates.filter((sc) => !addedSupportEmployeeIds.has(sc.employee_id)).map((sc) => (
                <button
                  key={sc.employee_id}
                  type="button"
                  onClick={() => setAddedSupportEmployeeIds((prev) => new Set(prev).add(sc.employee_id))}
                  className="min-h-[44px] inline-flex items-center gap-1 rounded-full border border-dashed border-surface-border px-2.5 text-xs text-text-body hover:border-brand-red hover:text-brand-red"
                >
                  <Plus size={11} aria-hidden />{sc.display_name}
                </button>
              ))}
            </div>
          )}
          {displayPeople.length === 0 ? (
            <EmptyState
              message="この店舗にはまだ社員が所属していません"
              description={supportCandidates.length > 0
                ? '上の「応援候補」から追加すると、ここに表示されます。'
                : '組織階層で社員を配置すると、ここに表示されます。'}
            />
          ) : (
            <>
              {zeroFlexSlotCount > 0 && (
                <p className="text-xs text-alert-text bg-alert-bg border border-alert-border rounded px-3 py-2">
                  融通枠ゼロの人が {zeroFlexSlotCount} 名います (週所定ぴったりで、1日でも変更が入ると満たせなくなります)。
                </p>
              )}
              {canManage && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="secondary" size="sm" loading={previewMut.isPending} onClick={() => previewMut.mutate()}>
                    曜日別の充足を確認
                  </Button>
                  {isPreviewStale && (
                    <span className="text-xs text-text-sub">内容が変更されました。表示中の結果は編集前のものです。</span>
                  )}
                </div>
              )}
              {isDirty && canManage && (
                <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3 sticky top-0 z-10">
                  <p className="text-xs text-alert-text">未保存の変更があります。</p>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={discard}>破棄</Button>
                    <Button variant="primary" size="sm" loading={diffPreviewMut.isPending} onClick={() => diffPreviewMut.mutate()}>保存</Button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto border border-surface-border rounded-lg bg-white">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-border">
                      <th scope="col" className="relative sticky left-0 bg-white text-left px-3 py-2 font-semibold text-text-sub whitespace-nowrap z-[1] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">従業員</th>
                      {DOW_LABELS.map((label, d) => (
                        <th key={d} scope="col" className={`px-2 py-2 font-semibold text-text-sub whitespace-nowrap min-w-[120px] ${
                          previewByDayOfWeek.get(d)?.satisfied === false && !isPreviewStale ? 'bg-alert-bg' : ''
                        }`}>{label}</th>
                      ))}
                      <th scope="col" className="px-2 py-2 font-semibold text-text-sub whitespace-nowrap">充足</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-line">
                    {preview && (
                      // v0.4 追加要件⑫ 増分④ (D-382): 曜日別充足プレビュー行。編集中グリッドを保存せずに
                      // 「この曜日は満たしているか」「不足なら誰を動かせるか」を確認する (-detail.md §3-3)。
                      // ⚠️ design-expert レビュー反映: 行見出しに scope="row" を使うため thead ではなく
                      // tbody の先頭行に置く (col見出しとrow見出しをthead内に混在させない)。
                      <tr className="border-b border-surface-border bg-surface-sunken/40">
                        <th scope="row" className="relative sticky left-0 bg-white text-left px-3 py-2 align-top text-xs font-semibold text-text-sub whitespace-nowrap z-[1] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                          必要人員充足
                          {isPreviewStale && <span className="block font-normal text-text-sub">内容が変更されました</span>}
                        </th>
                        {DOW_LABELS.map((_, d) => {
                          const day = previewByDayOfWeek.get(d);
                          if (!day) return <td key={d} className="px-2 py-2 align-top" />;
                          if (day.representative_date === null) {
                            return (
                              <td key={d} className="px-2 py-2 align-top">
                                <span className="text-[11px] text-text-sub">代表日なし (8週先まで評価不能)</span>
                              </td>
                            );
                          }
                          // ⚠️ design-expert レビュー反映 (H-1): stale 中は alert 系の色を使わず (再評価
                          // するまで「不足」を鵜呑みにさせない)、中立トーンに倒す。コントラスト再計算の
                          // リスクを避けるため opacity ではなく色トークンの切替で表現する。
                          const insufficient = day.satisfied === false && !isPreviewStale;
                          return (
                            <td key={d} className="px-2 py-2 align-top">
                              <div className="flex flex-col gap-1 items-start">
                                <span className={`text-[11px] px-1.5 py-0.5 rounded border inline-block w-fit ${
                                  insufficient ? 'bg-alert-bg text-alert-text border-alert-border' : 'bg-surface-sunken text-text-body border-surface-border'
                                }`}>
                                  {day.satisfied === false ? '人員不足' : '人員充足'} (代表日 {day.representative_date})
                                </span>
                                {day.satisfied === false && day.candidates_not_yet_scheduled.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {day.candidates_not_yet_scheduled.map((cand) => (
                                      <button key={`${cand.employee_id ?? ''}${cand.external_person_id ?? ''}`} type="button"
                                        disabled={!canManage || isPreviewStale}
                                        aria-label={`${DOW_LABELS[d]}曜日に${cand.display_name}を追加`}
                                        onClick={() => openCandidateEntry(d, cand)}
                                        className="min-h-[44px] min-w-[44px] px-2 py-1 text-[11px] rounded border border-surface-border bg-white text-text-body hover:border-brand-red focus-ring disabled:opacity-45 disabled:cursor-not-allowed">
                                        ＋{cand.display_name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2" />
                      </tr>
                    )}
                    {displayPeople.map((p) => {
                      const pKey = personKey(p);
                      const entriesForPerson = draft.filter((e) => personKey(e) === pKey);
                      const coverageInfo = coverageByPerson.get(pKey) ?? { coverage: null, flexSlots: null, isZeroDayContract: false };
                      return (
                        <tr key={pKey}>
                          <td className="relative sticky left-0 z-[1] bg-white px-3 py-2 align-top whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                            <div className="flex flex-col gap-1 items-start">
                              <span className="font-medium text-text-body">{p.display_name}</span>
                              {canManage && (
                                <Button variant="ghost" size="sm"
                                  onClick={() => setTemplateApplyContext({ employee_id: p.employee_id, external_person_id: p.external_person_id, display_name: p.display_name })}>
                                  テンプレートから展開
                                </Button>
                              )}
                            </div>
                          </td>
                          {DOW_LABELS.map((_, d) => {
                            const dayEntries = entriesForPerson.filter((e) => e.day_of_week === d);
                            return (
                              <td key={d} className="px-2 py-2 align-top">
                                <div className="flex flex-col gap-1 items-start">
                                  {dayEntries.map((e) => {
                                    // §8-221対策: サーバー解決済みの値 (廃止済みでも正しい) を優先し、
                                    // 無い (=この編集セッションで新規選択した) 場合のみ現行マスタ一覧から解決する。
                                    const level = levelById.get(e.preference_level_id);
                                    // design-expert HIGH反映: 現行マスタに無い (=廃止済み) ラベルは通常の
                                    // ラベルと同じ見た目のままだと紛らわしいため斜体+ツールチップで区別する
                                    // (attendance_reason_codesの集計UIで行った措置と同種)。孤児参照 (DBにも
                                    // 存在しないデータ異常) はMEDIUM反映で生ID文字列の代わりに日本語フォールバック。
                                    const isHistorical = !level;
                                    const label = e.preference_level_label ?? level?.label ?? '(削除済みの選好レベル)';
                                    const isAvailable = e.preference_level_is_available ?? level?.is_available;
                                    return (
                                      <button key={e.localId} type="button" disabled={!canManage} onClick={() => openEntry(pKey, d, e)}
                                        title={isHistorical ? `${label} (廃止済みの選好レベル)` : undefined}
                                        className={`text-[11px] px-1.5 py-1 rounded border min-h-[44px] text-left w-full ${
                                          isAvailable === false ? 'bg-alert-bg text-alert-text border-alert-border' : 'bg-surface-sunken text-text-body border-surface-border'
                                        }`}>
                                        {e.start_time}〜{e.end_time}
                                        <span className={`block text-text-sub ${isHistorical ? 'italic' : ''}`}>{label}</span>
                                      </button>
                                    );
                                  })}
                                  {canManage && (
                                    <button type="button" aria-label={`${p.display_name}の${DOW_LABELS[d]}曜日に枠を追加`} onClick={() => openEntry(pKey, d, null)}
                                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-brand-red hover:bg-surface-sunken focus-ring shrink-0">
                                      <Plus size={14} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-2 py-2 align-top whitespace-nowrap">
                            {coverageInfo.coverage == null ? (
                              <span className="text-xs text-text-sub">—</span>
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                <span className={`text-xs px-1.5 py-0.5 rounded border inline-block w-fit ${
                                  coverageInfo.coverage < 1 ? 'bg-alert-bg text-alert-text border-alert-border' : 'bg-surface-sunken text-text-body border-surface-border'
                                }`}>
                                  充足 {Math.round(coverageInfo.coverage * 100)}%
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded border inline-block w-fit ${
                                  coverageInfo.flexSlots === 0 && !coverageInfo.isZeroDayContract ? 'bg-alert-bg text-alert-text border-alert-border' : 'bg-surface-sunken text-text-body border-surface-border'
                                }`}>
                                  融通枠 {coverageInfo.flexSlots}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {editContext && (
        <EntryEditorModal
          context={editContext} levels={levels} presets={presets} hideManualInput={inputMode === 'pattern'}
          onClose={() => setEditContext(null)} onSave={applyEntry}
          onRemove={editContext.entry ? removeEntry : null} />
      )}

      {templateApplyContext && nodeId && (
        <ApplyTemplateModal
          nodeId={nodeId} personDisplayName={templateApplyContext.display_name}
          onClose={() => setTemplateApplyContext(null)} onApply={applyTemplateToPerson} />
      )}

      {/* v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): 保存前の差分・影響の確認ダイアログ。
          「確認と OK 出しの3層」の #2 (店長・保存時ダイアログ)。差分・影響はサーバーが計算した
          ものをそのまま表示するだけで、ここで送り返すことはしない (実際の保存は saveMut が
          entries を送り直しサーバーが自分で再計算する)。 */}
      <Modal
        open={!!confirmSave} title="この内容で保存しますか"
        onClose={() => { if (!saveMut.isPending) setConfirmSave(null); }}
        size="md"
        footer={confirmSave && (
          <>
            <Button variant="secondary" disabled={saveMut.isPending} onClick={() => setConfirmSave(null)}>キャンセル</Button>
            <Button variant="primary" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>この内容で保存する</Button>
          </>
        )}
      >
        {confirmSave && (
          <div className="space-y-4 text-sm">
            {confirmSave.diff_summary.persons.length === 0 ? (
              <p className="text-text-sub">変更点はありません。</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-text-sub">{confirmSave.diff_summary.changed_person_count}名の週間の枠が変わります。</p>
                <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                  {confirmSave.diff_summary.persons.map((p) => (
                    <li key={personKey({ employee_id: p.employee_id, external_person_id: p.external_person_id })}
                      className="border border-surface-line rounded p-2">
                      <p className="font-medium text-text-body">{p.display_name}</p>
                      {p.added.length > 0 && (
                        <p className="text-xs text-text-body mt-0.5">
                          追加: {p.added.map((a, i) => (
                            <span key={i}>{i > 0 && '、'}{DOW_LABELS[a.day_of_week]}曜日 {a.start_time}〜{a.end_time}</span>
                          ))}
                        </p>
                      )}
                      {p.removed.length > 0 && (
                        <p className="text-xs text-text-sub mt-0.5">
                          削除: {p.removed.map((r, i) => (
                            <span key={i}>{i > 0 && '、'}{DOW_LABELS[r.day_of_week]}曜日 {r.start_time}〜{r.end_time}</span>
                          ))}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {confirmSave.impact_summary.contracted.length > 0 && (
              <div className="space-y-1 pt-2 border-t border-surface-border">
                <p className="text-xs font-semibold text-text-sub">充足・融通枠への影響</p>
                <ul className="space-y-0.5">
                  {confirmSave.impact_summary.contracted.map((ct) => {
                    const person = confirmSave.diff_summary.persons.find((p) => p.employee_id === ct.employee_id);
                    return (
                      <li key={ct.employee_id} className="text-xs text-text-body">
                        {person?.display_name ?? ct.employee_id}: 充足 {formatCoveragePercent(ct.before)} → {formatCoveragePercent(ct.after)}
                        {'　'}融通枠 {ct.flex_slots_before ?? '—'} → {ct.flex_slots_after ?? '—'}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {confirmSave.impact_summary.staffing_by_dow.length > 0 && (
              <div className="space-y-1 pt-2 border-t border-surface-border">
                <p className="text-xs font-semibold text-text-sub">必要人員への影響 (代表日での近似、§4-2)</p>
                <ul className="space-y-1">
                  {confirmSave.impact_summary.staffing_by_dow.map((d) => (
                    <li key={d.day_of_week} className="text-xs text-text-body">
                      {DOW_LABELS[d.day_of_week]}曜日:{' '}
                      <SatisfiedLabel satisfied={d.satisfied_before} />
                      {' → '}
                      <SatisfiedLabel satisfied={d.satisfied_after} emphasize />
                      {d.representative_date && <span className="text-text-sub"> (代表日 {d.representative_date})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
